/**
 * Pure, immutable helpers for editing a page AST (ADR-0010 P7c1).
 *
 * The builder's tree mutations live here so the reducer-ish logic is
 * unit-tested without React. Every function returns a NEW root (structural
 * clone of the touched path) — never mutates the input.
 */
import type { PageComponentSpec, PageNode } from "./pageTypes";

/** All node ids currently in the tree. */
export function collectIds(root: PageNode): Set<string> {
  const out = new Set<string>();
  const walk = (n: PageNode) => {
    out.add(n.id);
    n.children.forEach(walk);
  };
  walk(root);
  return out;
}

/** A short id unique within `used` (deterministic — no Date/Math.random). */
export function genId(used: Set<string>): string {
  let i = 1;
  while (used.has(`n${i}`)) i++;
  return `n${i}`;
}

/** A node id must be identifier-shaped (so it's safe to type in JS as
 *  `openModal("…")` and to splice into bindings) and unique within the page.
 *  Returns an error CODE (`empty` | `shape` | `duplicate`) or null when valid.
 *  `oldId === newId` is always valid (a no-op rename). */
const NODE_ID_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
export function validateNodeId(root: PageNode, oldId: string, candidate: string): string | null {
  const id = candidate.trim();
  if (!id) return "empty";
  if (!NODE_ID_RE.test(id)) return "shape";
  if (id === oldId) return null;
  if (collectIds(root).has(id)) return "duplicate";
  return null;
}

/** Rename a node's id across the whole page (ADR-0045): the node itself PLUS
 *  every `open_modal` / `close_modal` action `target` that pointed at it — the
 *  only user-facing by-node-id reference. Pure; a no-op when `oldId === newId`.
 *  The caller must validate uniqueness first (`validateNodeId`). */
export function renameNodeId(root: PageNode, oldId: string, newId: string): PageNode {
  if (oldId === newId) return root;
  const fixTargets = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(fixTargets);
    if (value && typeof value === "object") {
      const next: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) next[k] = fixTargets(v);
      if ((next.type === "open_modal" || next.type === "close_modal") && next.target === oldId) {
        next.target = newId;
      }
      return next;
    }
    return value;
  };
  return mapTree(root, (n) => {
    const props = fixTargets(n.props) as Record<string, unknown>;
    return n.id === oldId ? { ...n, id: newId, props } : { ...n, props };
  });
}

/** Rename a JS Object across the whole page (ADR-0044 J1) when its name changes
 *  in the editor: rewrites every reference so a rename doesn't silently break the
 *  bindings (the JS-Object twin of `renameNodeId`). Two reference shapes:
 *   - `{{ … Old.fn(…) … }}` bindings in ANY string prop — the namespace `Old`
 *     used as a member base INSIDE a `{{ }}` segment (so a literal "Old.x" in
 *     plain text is never touched);
 *   - a `run_js` action whose `fn` is `"Old.method"` — the `Old.` prefix.
 *  Pure; a no-op when `oldName === newName`. */
export function renameJsObjectRefs(root: PageNode, oldName: string, newName: string): PageNode {
  if (oldName === newName) return root;
  const esc = oldName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // `Old` as a top-level identifier (not preceded by `.`/word/`$`) immediately
  // before a member access `.` — i.e. a JS-Object call `Old.fn`.
  const ref = new RegExp(`(?<![.\\w$])${esc}\\b(?=\\s*\\.)`, "g");
  const fixStr = (s: string): string =>
    // only rewrite inside `{{ … }}` segments (page expressions), never plain text
    s.replace(/\{\{([^}]*)\}\}/g, (_m, inner: string) => `{{${inner.replace(ref, newName)}}}`);
  const fixVal = (value: unknown): unknown => {
    if (typeof value === "string") return fixStr(value);
    if (Array.isArray(value)) return value.map(fixVal);
    if (value && typeof value === "object") {
      const obj = value as Record<string, unknown>;
      const next: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(obj)) next[k] = fixVal(v);
      // run_js action: fn "Old" / "Old.method" → swap the namespace prefix.
      if (
        next.type === "run_js" &&
        typeof next.fn === "string" &&
        (next.fn === oldName || next.fn.startsWith(`${oldName}.`))
      ) {
        next.fn = `${newName}${(next.fn as string).slice(oldName.length)}`;
      }
      return next;
    }
    return value;
  };
  return mapTree(root, (n) => ({ ...n, props: fixVal(n.props) as Record<string, unknown> }));
}

export function findNode(root: PageNode, id: string): PageNode | null {
  if (root.id === id) return root;
  for (const c of root.children) {
    const hit = findNode(c, id);
    if (hit) return hit;
  }
  return null;
}

/** Find the parent of `id` (null for the root or a missing id). */
export function findParent(root: PageNode, id: string): PageNode | null {
  for (const c of root.children) {
    if (c.id === id) return root;
    const hit = findParent(c, id);
    if (hit) return hit;
  }
  return null;
}

/** Mint a new node of `spec`'s type with default props from its schema. */
export function newNode(spec: PageComponentSpec, used: Set<string>): PageNode {
  const props: Record<string, unknown> = {};
  for (const [key, schema] of Object.entries(spec.props_schema.properties ?? {})) {
    if (schema.default !== undefined) props[key] = schema.default;
  }
  return { id: genId(used), type: spec.type, props, children: [] };
}

/** Map every node through `fn` (post-order on the cloned tree). */
function mapTree(root: PageNode, fn: (n: PageNode) => PageNode): PageNode {
  const mapped: PageNode = {
    ...root,
    children: root.children.map((c) => mapTree(c, fn)),
  };
  return fn(mapped);
}

export function addChild(root: PageNode, parentId: string, child: PageNode): PageNode {
  return mapTree(root, (n) =>
    n.id === parentId ? { ...n, children: [...n.children, child] } : n,
  );
}

export function removeNode(root: PageNode, id: string): PageNode {
  // The root cannot be removed.
  return mapTree(root, (n) => ({
    ...n,
    children: n.children.filter((c) => c.id !== id),
  }));
}

export function updateProps(
  root: PageNode,
  id: string,
  props: Record<string, unknown>,
): PageNode {
  return mapTree(root, (n) => (n.id === id ? { ...n, props } : n));
}

/** Set node `id`'s conditional-visibility expression. An empty/blank value
 *  drops the `visible_when` key so the node round-trips as always-visible. */
export function setVisibleWhen(root: PageNode, id: string, expr: string): PageNode {
  const blank = !expr || expr.trim() === "";
  return mapTree(root, (n) => {
    if (n.id !== id) return n;
    if (blank) {
      const { visible_when: _drop, ...rest } = n;
      return rest as PageNode;
    }
    return { ...n, visible_when: expr };
  });
}

/** Replace the shared style tokens of node `id` (S15). An empty map drops the
 *  `style` key entirely so the node round-trips as un-styled. */
export function updateStyle(
  root: PageNode,
  id: string,
  style: Record<string, unknown>,
): PageNode {
  const isEmpty = Object.keys(style).length === 0;
  return mapTree(root, (n) => {
    if (n.id !== id) return n;
    if (isEmpty) {
      const { style: _drop, ...rest } = n;
      return rest as PageNode;
    }
    return { ...n, style };
  });
}

/** Move `id` among its siblings by `dir` (-1 up, +1 down); no-op at edges. */
export function moveSibling(root: PageNode, id: string, dir: -1 | 1): PageNode {
  return mapTree(root, (n) => {
    const idx = n.children.findIndex((c) => c.id === id);
    if (idx === -1) return n;
    const swap = idx + dir;
    if (swap < 0 || swap >= n.children.length) return n;
    const next = [...n.children];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    return { ...n, children: next };
  });
}

/** Insert `child` into `parentId`'s children at `index` (clamped). Used when
 *  dropping a NEW component from the palette at a precise position. */
export function insertChildAt(
  root: PageNode,
  parentId: string,
  index: number,
  child: PageNode,
): PageNode {
  return mapTree(root, (n) => {
    if (n.id !== parentId) return n;
    const at = Math.max(0, Math.min(index, n.children.length));
    const next = [...n.children];
    next.splice(at, 0, child);
    return { ...n, children: next };
  });
}

/**
 * Move `movingId` to land **before** or **after** `targetId` under the same
 * parent (drag-reorder with an insertion line). Detaches first, then inserts
 * relative to the target's *post-detach* position — so it's free of the
 * classic off-by-one when the source preceded the target. No-op for illegal
 * moves (root, self, into own descendant, or a target with no parent).
 */
export function moveRelative(
  root: PageNode,
  movingId: string,
  targetId: string,
  position: "before" | "after",
): PageNode {
  if (movingId === root.id || movingId === targetId) return root;
  const moving = findNode(root, movingId);
  if (!moving || !findNode(root, targetId)) return root;
  if (findNode(moving, targetId)) return root; // can't drop into own subtree
  const detached = removeNode(root, movingId);
  const parent = findParent(detached, targetId);
  if (!parent) return root; // target is the root → it has no siblings
  return mapTree(detached, (n) => {
    if (n.id !== parent.id) return n;
    const idx = n.children.findIndex((c) => c.id === targetId);
    const at = position === "before" ? idx : idx + 1;
    const next = [...n.children];
    next.splice(at, 0, moving);
    return { ...n, children: next };
  });
}

/** Deep-clone a subtree, assigning every node a fresh id (avoiding `used`). */
function cloneWithNewIds(node: PageNode, used: Set<string>): PageNode {
  const id = genId(used);
  used.add(id);
  return {
    ...node,
    id,
    props: { ...node.props },
    children: node.children.map((c) => cloneWithNewIds(c, used)),
  };
}

/**
 * Duplicate the subtree at `id`, inserting the copy right after it under the
 * same parent (S6 builder). Every node in the copy gets a fresh id. Returns
 * the root unchanged for the root node or an unknown id. Returns the new
 * top-level node's id via the out-param `outNewId` (first element).
 */
export function duplicateNode(
  root: PageNode,
  id: string,
  outNewId?: string[],
): PageNode {
  if (id === root.id) return root;
  const parent = findParent(root, id);
  if (!parent) return root;
  const original = findNode(root, id);
  if (!original) return root;
  const used = collectIds(root);
  const copy = cloneWithNewIds(original, used);
  if (outNewId) outNewId[0] = copy.id;
  return mapTree(root, (n) => {
    if (n.id !== parent.id) return n;
    const idx = n.children.findIndex((c) => c.id === id);
    const next = [...n.children];
    next.splice(idx + 1, 0, copy);
    return { ...n, children: next };
  });
}

/**
 * Drag-drop primitive (P7c2): detach `id` and re-insert it as a child of
 * `newParentId` at position `index` (clamped; `index < 0` ⇒ append).
 *
 * Returns the root unchanged for any illegal move: the root itself, an
 * unknown id/parent, or dropping a node into itself or one of its own
 * descendants (which would orphan the subtree). The detach+insert is a
 * single pass so an in-place reorder (same parent) lands at the right slot
 * after the source is removed.
 */
/**
 * Enforce the tabs invariant when resolving a builder drop: a `tabs` container
 * holds ONLY `tab` children, so every other component must land INSIDE a tab's
 * panel. The Elementor-style drop geometry would otherwise turn a near-the-edge
 * drop on a tab (before/after → sibling) or a drop on the tab bar (inside tabs)
 * into a stray new tab. Re-aim any non-`tab` payload that would otherwise land
 * directly under a `tabs` node into that tabs' visible (selected) tab.
 *
 * Returns the (possibly rewritten) `{ targetId, position }`. When the target
 * `tabs` has no tab yet, returns `null` — there is nowhere to place content,
 * so the caller should drop a Tab first.
 */
export function reaimTabsDrop(
  root: PageNode,
  droppedType: string | undefined,
  targetId: string,
  position: "before" | "after" | "inside",
  selectedId: string,
): { targetId: string; position: "before" | "after" | "inside" } | null {
  if (droppedType === "tab") return { targetId, position };
  const directParentId =
    position === "inside" ? targetId : findParent(root, targetId)?.id;
  const directParent = directParentId ? findNode(root, directParentId) : null;
  if (directParent?.type !== "tabs") return { targetId, position };
  const tabs = directParent.children;
  if (tabs.length === 0) return null; // empty tabs: drop a Tab first.
  // The builder reveals the tab that contains the current selection; fall back
  // to the first tab when the selection lives elsewhere.
  const visible = tabs.find((t) => findNode(t, selectedId)) ?? tabs[0];
  return { targetId: visible.id, position: "inside" };
}

/** Is `node` a horizontal-flowing container (the unified `container` in row or
 *  grid layout)? Such a node lays its children out left-to-right, so dropping on
 *  a child's side edge widens it instead of wrapping. */
export function isHorizontalContainer(node: PageNode | null | undefined): boolean {
  if (!node || node.type !== "container") return false;
  const layout = String(node.props.layout ?? "stack");
  return layout === "row" || layout === "grid";
}

/**
 * Horizontal "drop on the side edge" → side-by-side (Elementor-style auto-wrap).
 * `moving` is the dropped node (a fresh palette node, or an existing node which
 * is detached from its current slot first). `side` is which side of `targetId`
 * it lands on.
 *
 * Two cases:
 *  - target already sits in a horizontal `container` (row/grid) → insert `moving`
 *    as a new adjacent cell; for a grid, widen `columns` so it grows a column
 *    (doesn't wrap to a 2nd row). This is how "drop again on an edge adds a
 *    column" works.
 *  - target is anywhere else (stack / card / page) → replace it in place with a
 *    fresh 2-column grid `container` (id `newColumnsId`) holding `[moving,
 *    target]` (left) or `[target, moving]` (right).
 *
 * No-op (returns the input root) when target is the root, equals `moving`, or
 * lives inside `moving`'s own subtree.
 */
export function dropSideBySide(
  root: PageNode,
  targetId: string,
  moving: PageNode,
  side: "left" | "right",
  newColumnsId: string,
): PageNode {
  if (moving.id === targetId) return root;
  if (findNode(moving, targetId)) return root; // can't wrap into your own subtree
  // Detach an existing `moving`; a fresh (not-yet-in-tree) node is a no-op here.
  const base = findNode(root, moving.id) ? removeNode(root, moving.id) : root;
  const parent = findParent(base, targetId);
  if (!parent) return root; // target is the root → it has no siblings to grid with

  if (isHorizontalContainer(parent)) {
    const idx = parent.children.findIndex((c) => c.id === targetId);
    const at = side === "left" ? idx : idx + 1;
    const widened = insertChildAt(base, parent.id, at, moving);
    // For a grid, grow `columns` so the new cell sits on the same row (a flex
    // row needs no count — it grows naturally).
    return mapTree(widened, (n) =>
      n.id === parent.id && String(n.props.layout) === "grid"
        ? { ...n, props: { ...n.props, columns: Math.min(6, n.children.length) } }
        : n,
    );
  }

  // Wrap target + moving into a fresh 2-column grid container in target's slot.
  return mapTree(base, (n) => {
    if (n.id !== parent.id) return n;
    const next = n.children.map((c) => {
      if (c.id !== targetId) return c;
      return {
        id: newColumnsId,
        type: "container",
        props: { layout: "grid", columns: 2 },
        children: side === "left" ? [moving, c] : [c, moving],
      } as PageNode;
    });
    return { ...n, children: next };
  });
}

export function moveNode(
  root: PageNode,
  id: string,
  newParentId: string,
  index: number,
): PageNode {
  if (id === root.id || id === newParentId) return root;
  const moving = findNode(root, id);
  const target = findNode(root, newParentId);
  if (!moving || !target) return root;
  // Reject dropping a node inside its own subtree.
  if (findNode(moving, newParentId)) return root;

  // Detach: strip `id` from wherever it currently lives.
  const detached = removeNode(root, id);
  // Insert under the (possibly re-cloned) target.
  return mapTree(detached, (n) => {
    if (n.id !== newParentId) return n;
    const at = index < 0 || index > n.children.length ? n.children.length : index;
    const next = [...n.children];
    next.splice(at, 0, moving);
    return { ...n, children: next };
  });
}

/**
 * Should the builder ADOPT a freshly-refetched server copy of the page over its
 * local working copy? True only when the operator has NO unsaved edits (`dirty`
 * is false) AND the server copy actually differs. Used by `PageBuilderPage` to
 * live-refresh the canvas when Odino edits THIS page through its own session
 * (ADR-0024 slice D) — without ever clobbering unsaved local work. Compares by
 * value (deep), not reference: a refetch always yields a new object.
 */
export function shouldAdoptServerCopy(
  serverAst: unknown,
  localAst: unknown,
  dirty: boolean,
): boolean {
  if (dirty) return false;
  return JSON.stringify(serverAst) !== JSON.stringify(localAst);
}
